import { useState, useEffect, useCallback, KeyboardEventHandler } from "react";
import { TablePaginationProps } from "@mui/material/TablePagination";
import { SelectChangeEvent, SelectProps } from "@mui/material/Select";
import { DataGridProps } from "@mui/x-data-grid";
import { ICloseEvent } from "@core/interfaces/ICloseEvent";
import { useStore } from "@ui/hooks/useStore";
import useKeyDown from "@ui/hooks/useKeyDown";
import useDictionary from "@ui/hooks/useDictionary";

export interface IErrorMap {
    imei: string[];
    type: string[];
    password: string[];
    swVer: string[];
    bootVer: string[];
    hwVer: string[];
}

export interface IRow {
    id: number;
    imei: string;
    type: string;
    password: string;
    swVer: string;
    bootVer: string;
    hwVer: string;
}

export interface IColumn {
    field: string;
    headerName: string;
    width: number;
    minWidth: number;
    flex: number;
    editable: boolean;
    sortable: boolean;
    filterable: boolean;
}

export interface IState {
    loading: boolean;
    page: number;
    limit: number;
    rows: IRow[];
    columns: IColumn[];
    uploadError: boolean;
    errorMap: IErrorMap;
}

const useAddDeviceInDbTable = (onClose: ICloseEvent["onClose"]) => {
    const { download, lang, devices } = useStore();
    const getWord = useDictionary();
    const key = useKeyDown();

    const devicesToAdd = download.uploadedDevices ?? [];
    const colData = [
        { field: "imei", width: 130 },
        { field: "type", width: 130 },
        { field: "password", width: 130 },
        { field: "swVer", width: 130 },
        { field: "bootVer", width: 130 },
        { field: "hwVer", width: 130 },
    ];
    const columns = colData.map(({ field, width }) => {
        let headerName = getWord(field);
        return {
            field,
            headerName,
            width,
            minWidth: width,
            flex: 1,
            editable: true,
            sortable: false,
            filterable: false,
        };
    });

    const rows = devicesToAdd.map((data, i) => {
        const id = i;
        const { imei, type, password, swVer, bootVer, hwVer } = data;
        return {
            id,
            imei,
            type,
            password,
            swVer,
            bootVer,
            hwVer,
        };
    });

    const initState: IState = {
        loading: false,
        page: 0,
        limit: 50,
        rows,
        columns,
        uploadError: false,
        errorMap: {
            imei: [],
            type: [],
            password: [],
            swVer: [],
            bootVer: [],
            hwVer: [],
        },
    };
    const [state, setState] = useState(initState);

    const imeiValidate = (imei: string) => {
        const regExp = /^\d{15,16}$/;
        const doubledImei = state.rows
            .map(({ imei }) => imei)
            .filter((imei, i, arr) => arr.indexOf(imei) !== i)
            .filter((imei, i, arr) => arr.indexOf(imei) === i);
        if (!imei || !regExp.test(imei) || doubledImei.indexOf(imei) > -1) {
            setState((prev) => ({
                ...prev,
                errorMap: {
                    ...prev.errorMap,
                    imei: [...prev.errorMap.imei, imei],
                },
            }));
            return false;
        } else return true;
    };
    const typeValidate = (type: string) => {
        if (!type) {
            setState((prev) => ({
                ...prev,
                errorMap: {
                    ...prev.errorMap,
                    type: [...prev.errorMap.type, type],
                },
            }));
            return false;
        } else return true;
    };
    const passwordValidate = (password: string) => {
        if (!password) {
            setState((prev) => ({
                ...prev,
                errorMap: {
                    ...prev.errorMap,
                    password: [...prev.errorMap.password, password],
                },
            }));
            return false;
        } else return true;
    };
    const swVerValidate = (swVer: string) => {
        const regExp = /^\d+\.\d+$/;
        if (!swVer || !regExp.test(swVer)) {
            setState((prev) => ({
                ...prev,
                errorMap: {
                    ...prev.errorMap,
                    swVer: [...prev.errorMap.swVer, swVer],
                },
            }));
            return false;
        } else return true;
    };

    const bootVerValidate = (bootVer: string) => {
        const regExp = /^\d+\.\d+$/;
        if (!bootVer || !regExp.test(bootVer)) {
            setState((prev) => ({
                ...prev,
                errorMap: {
                    ...prev.errorMap,
                    bootVer: [...prev.errorMap.bootVer, bootVer],
                },
            }));
            return false;
        } else return true;
    };

    const hwVerValidate = (hwVer: string) => {
        if (!hwVer) {
            setState((prev) => ({
                ...prev,
                errorMap: {
                    ...prev.errorMap,
                    type: [...prev.errorMap.type, hwVer],
                },
            }));
            return false;
        } else return true;
    };

    const handlePageChange: TablePaginationProps["onPageChange"] = useCallback(
        (e, page) => {
            setState((prev) => ({ ...prev, page }));
        },
        []
    );
    const handleCancelClick = useCallback(() => {
        onClose();
    }, []);
    const handleAddClick = useCallback(async () => {
        const isValid = state.rows
            .map((device) => {
                const { imei, type, swVer, bootVer, hwVer } = device;
                const isImeiValid = imeiValidate(imei);
                const isTypeValid = typeValidate(type);
                const isSwVerValid = swVerValidate(swVer);
                const isHwVerValid = hwVerValidate(hwVer);
                const isBootVerValid = bootVerValidate(bootVer);
                return (
                    isImeiValid && isTypeValid && isSwVerValid && isHwVerValid && isBootVerValid
                );
            })
            .every((val) => val);
        if (isValid) {
            devices.resetErrorDevice();
            setState((prev) => ({ ...prev, loading: true }));
            await Promise.all(
                state.rows.map(async (device) => {
                    const { imei, type, password, swVer, bootVer, hwVer } =
                        device;
                    const [
                        application_version_major,
                        application_version_minor,
                    ] = swVer.split(".");
                    const [bootloader_version_major, bootloader_version_minor] =
                        bootVer.split(".");
                    const args = {
                        imei,
                        type,
                        password,
                        application_version_major,
                        application_version_minor,
                        bootloader_version_major,
                        bootloader_version_minor,
                        hw_version: hwVer,
                    };
                    return await devices.addDeviceInDb(args);
                })
            );
            const { serverError, errorDevices } = devices;
            const isError = serverError;
            setState((prev) => ({ ...prev, loading: false }));
            if (isError) {
                setState((prev) => ({
                    ...prev,
                    rows: prev.rows
                        .filter(({ imei }) => errorDevices.indexOf(imei) > -1)
                        .map((item, id) => ({ ...item, id })),
                    errorMap: {
                        ...prev.errorMap,
                        imei: [...errorDevices],
                    },
                }));
            } else {
                devices.resetOperationSuccess();
                onClose();
            }
        } else setState((prev) => ({ ...prev, uploadError: true }));
    }, [state]);
    const handleCellEdit: DataGridProps["onEditCellPropsChange"] = useCallback(
        (params: any) => {
            setState((prev) => ({
                ...prev,
                rows: prev.rows.map((row, i) => {
                    if (params.id === i) {
                        const field = params.field;
                        const result = { ...row };
                        if (field !== "id") {
                            const _result = result as Omit<IRow, "id">;
                            _result[field as keyof typeof _result] =
                                params.props.value;
                        }
                        return result;
                    } else return row;
                }),
                errorMap: initState.errorMap,
                uploadError: false,
            }));
            devices.resetServerError();
        },
        []
    );
    const handleCellEditStart = useCallback(() => {
        setState((prev) => ({
            ...prev,
            errorMap: initState.errorMap,
            uploadError: false,
        }));
        devices.resetServerError();
    }, []);
    const handleCellEditStop = useCallback(() => {
        setState((prev) => ({
            ...prev,
            errorMap: initState.errorMap,
            uploadError: false,
        }));
        devices.resetServerError();
    }, []);
    const handleKey = (key: string) => {
        switch (key) {
            case "Escape":
                onClose();
                break;
            case "Enter":
                handleAddClick();
                break;
        }
    };
    const handleKeyDown: KeyboardEventHandler<HTMLElement> = useCallback(
        (e) => {
            const { key } = e;
            if (key === "Enter") {
                e.stopPropagation();
                e.preventDefault();
                handleKey(key);
            }
        },
        []
    );
    const handleBtnKeyDown: KeyboardEventHandler<HTMLButtonElement> =
        useCallback((e) => {
            const { key } = e;
            if (key === "Enter") {
                e.stopPropagation();
                e.preventDefault();
                const target = e.target as HTMLButtonElement;
                target.click();
            }
        }, []);
    const handeLimitChange = useCallback(
        ({ target }: SelectChangeEvent<number>) => {
            let limit: number;
            const { value } = target;
            if (typeof value === "number") {
                limit = value;
            } else if (typeof value === "string") {
                const regExp = /^(\d)*/;
                if (!regExp.test(value)) return;
                limit = parseInt(value, 10);
                if (isNaN(limit)) return;
            } else {
                return;
            }
            setState((prev) => ({ ...prev, page: 0, limit }));
        },
        []
    );
    const handlers = {
        handlePageChange,
        handleCancelClick,
        handleAddClick,
        handleCellEdit,
        handleCellEditStart,
        handleCellEditStop,
        handleKeyDown,
        handleBtnKeyDown,
        handeLimitChange,
    };

    useEffect(() => {
        if (devices.operationSuccess) {
            onClose();
            devices.resetOperationSuccess();
        }
    }, [devices.operationSuccess]);
    useEffect(() => {
        return () => {
            devices.resetServerError();
            download.resetUploadedDevices();
            devices.resetErrorDevice();
        };
    }, []);
    useEffect(() => {
        handleKey(key);
    }, [key]);

    return {
        download,
        devices,
        lang,
        state,
        handlers,
    };
};

export default useAddDeviceInDbTable;
